import React from 'react';

interface ResourceStatusDisplayProps {
  title: string;
  used: number;
  available: number;
  unit: string;
  criticalThreshold?: number; // % of usage that is considered critical (red)
  warningThreshold?: number;  // % of usage that is considered warning (yellow)
}

const ResourceStatusDisplay: React.FC<ResourceStatusDisplayProps> = ({
  title,
  used,
  available,
  unit,
  criticalThreshold = 90,
  warningThreshold = 70
}) => {
  // Calculate usage percentage
  const usagePercentage = available > 0 ? (used / available) * 100 : 0;
  
  // Determine status color based on thresholds
  let statusColor = 'bg-green-500';
  let textColor = 'text-green-500';
  
  if (usagePercentage >= criticalThreshold) {
    statusColor = 'bg-red-500';
    textColor = 'text-red-500';
  } else if (usagePercentage >= warningThreshold) {
    statusColor = 'bg-yellow-500';
    textColor = 'text-yellow-500';
  }

  return (
    <div className="mb-4 bg-gray-800 rounded-lg p-4">
      <div className="flex justify-between items-center mb-1">
        <h3 className="text-lg font-semibold">{title}</h3>
        <div className={`font-medium ${textColor}`}>
          {used.toFixed(1)} / {available.toFixed(1)} {unit}
        </div>
      </div>
      
      <div className="w-full bg-gray-700 rounded-full h-2.5">
        <div 
          className={`${statusColor} h-2.5 rounded-full transition-all duration-300 ease-in-out`} 
          style={{ width: `${Math.min(usagePercentage, 100)}%` }}
        ></div>
      </div>
      
      <div className="flex justify-between text-xs text-gray-400 mt-1">
        <span>{usagePercentage.toFixed(1)}% used</span>
        <span>{Math.max(available - used, 0).toFixed(1)} {unit} remaining</span>
      </div>
    </div>
  );
};

export default ResourceStatusDisplay;
